<template>
  <div class="location-social-metrics-table">
    <div class="location-social-metrics-table__heading">
      <h2 class="global-h4 location-social-metrics-table__title">{{ props.title }}</h2>

      <input v-if="props.isSearchVisible" class="global-text-input location-social-metrics-table__search" type="text" placeholder="Search locations" v-model="search" />
    </div>

    <div class="location-social-metrics-table__scroll">
      <table class="location-social-metrics-table__table">
        <thead>
          <tr class="location-social-metrics-table__header-row">
            <th class="location-social-metrics-table__th location-social-metrics-table__th--sort" :aria-sort="ariaSort('name')" @click="sortByColumn('name')">
              <span class="location-social-metrics-table__th-inner location-social-metrics-table__th-inner--start">
                Location
                <IconEmbedded v-if="sortKey === 'name'" class="location-social-metrics-table__caret" :name="sortCaret" :size="10" />
              </span>
            </th>
            <th class="location-social-metrics-table__th location-social-metrics-table__th--networks">Networks</th>
            <th class="location-social-metrics-table__th location-social-metrics-table__th--number location-social-metrics-table__th--sort" :aria-sort="ariaSort('featuredPostCount')" @click="sortByColumn('featuredPostCount')">
              <span class="location-social-metrics-table__th-inner">
                Posts
                <IconEmbedded v-if="sortKey === 'featuredPostCount'" class="location-social-metrics-table__caret" :name="sortCaret" :size="10" />
              </span>
            </th>
            <th class="location-social-metrics-table__th location-social-metrics-table__th--number location-social-metrics-table__th--sort" :aria-sort="ariaSort('reach')" @click="sortByColumn('reach')">
              <span class="location-social-metrics-table__th-inner">
                Reach
                <IconEmbedded v-if="sortKey === 'reach'" class="location-social-metrics-table__caret" :name="sortCaret" :size="10" />
                <span class="location-social-metrics-table__info" @click.stop>
                  <InfoIconWithTooltip icon="info_1" :iconSize="15" :width="230">Unique accounts that saw a post featuring this location.</InfoIconWithTooltip>
                </span>
              </span>
            </th>
            <th class="location-social-metrics-table__th location-social-metrics-table__th--number location-social-metrics-table__th--sort" :aria-sort="ariaSort('engagements')" @click="sortByColumn('engagements')">
              <span class="location-social-metrics-table__th-inner">
                Engagements
                <IconEmbedded v-if="sortKey === 'engagements'" class="location-social-metrics-table__caret" :name="sortCaret" :size="10" />
                <span class="location-social-metrics-table__info" @click.stop>
                  <InfoIconWithTooltip icon="info_1" :iconSize="15" :width="230">Reactions, comments, shares and saves on those posts.</InfoIconWithTooltip>
                </span>
              </span>
            </th>
            <th class="location-social-metrics-table__th location-social-metrics-table__th--number location-social-metrics-table__th--sort" :aria-sort="ariaSort('engagementRatePercent')" @click="sortByColumn('engagementRatePercent')">
              <span class="location-social-metrics-table__th-inner">
                Rate
                <IconEmbedded v-if="sortKey === 'engagementRatePercent'" class="location-social-metrics-table__caret" :name="sortCaret" :size="10" />
              </span>
            </th>
            <th class="location-social-metrics-table__th location-social-metrics-table__th--action"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!visibleRows.length">
            <td class="location-social-metrics-table__empty-cell" colspan="7">No locations match</td>
          </tr>
          <tr v-for="row in visibleRows" :key="row.locationId" class="location-social-metrics-table__row">
            <td class="location-social-metrics-table__td">
              <div class="location-social-metrics-table__location">
                <LocationAvatar :src="row.icon" :name="row.name" :size="32" />
                <span class="location-social-metrics-table__name">{{ row.name }}</span>
              </div>
            </td>
            <td class="location-social-metrics-table__td location-social-metrics-table__td--networks">
              <NetworkIcons v-if="row.channels?.length" :channels="row.channels" :size="18" />
              <span v-else class="location-social-metrics-table__muted">—</span>
            </td>
            <td class="location-social-metrics-table__td location-social-metrics-table__td--number">{{ row.featuredPostCount }}</td>
            <td class="location-social-metrics-table__td location-social-metrics-table__td--number">{{ formatCount(row.reach) }}</td>
            <td class="location-social-metrics-table__td location-social-metrics-table__td--number">{{ formatCount(row.engagements) }}</td>
            <td class="location-social-metrics-table__td location-social-metrics-table__td--number">{{ formatRate(row.engagementRatePercent) }}</td>
            <td class="location-social-metrics-table__td location-social-metrics-table__td--action">
              <LinkWithIcon v-if="row.featuredPostCount > 0" isDottedUnderline color="gray" @click="emit('viewPosts', row.locationId)">View posts ›</LinkWithIcon>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <LinkWithIcon v-if="isShowingAllLink" class="location-social-metrics-table__view-all" isDottedUnderline color="gray" @click="emit('viewAllLocations')">
      View all {{ searchedRows.length }} locations
    </LinkWithIcon>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import NumberFormatting from "@logic/NumberFormatting";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import NetworkIcons from "./NetworkIcons.vue";

// Types
import type { LocationSocialMetricsRow } from "@contracts/socialReports";
import type { IconName } from "@contracts/IconName";

type SortKey = "name" | "featuredPostCount" | "reach" | "engagements" | "engagementRatePercent";
type SortDir = "asc" | "desc";

const props = withDefaults(
  defineProps<{
    rows: LocationSocialMetricsRow[];
    title?: string;
    /** Overview passes a limit to show only the top rows; the Locations tab leaves it null to show all. */
    limit?: number | null;
    isSearchVisible?: boolean;
  }>(),
  {
    title: "Results by location",
    limit: null,
    isSearchVisible: true,
  }
);

const emit = defineEmits<{
  (e: "viewPosts", locationId: string): void;
  (e: "viewAllLocations"): void;
}>();

const search = ref<string>("");
const sortKey = ref<SortKey | null>(null);
const sortDir = ref<SortDir>("desc");

function clearSearch() {
  search.value = "";
}

defineExpose({ clearSearch });

function defaultDir(key: SortKey): SortDir {
  return key === "name" ? "asc" : "desc";
}

function sortByColumn(key: SortKey) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
    return;
  }
  sortKey.value = key;
  sortDir.value = defaultDir(key);
}

function ariaSort(key: SortKey): "ascending" | "descending" | "none" {
  if (sortKey.value !== key) return "none";
  return sortDir.value === "asc" ? "ascending" : "descending";
}

const sortCaret = computed<IconName>(() => (sortDir.value === "asc" ? "caret-top_2-5" : "caret-bottom_2-5"));

// Rows are pre-sorted by the backend (reach desc) until a header is clicked.
const searchedRows = computed<LocationSocialMetricsRow[]>(() => {
  const query = search.value.trim().toLowerCase();
  const filtered = query ? props.rows.filter(r => (r.name ?? "").toLowerCase().includes(query)) : props.rows;
  if (!sortKey.value) return filtered;

  const key = sortKey.value;
  const dir = sortDir.value === "asc" ? 1 : -1;
  return [...filtered].sort((a, b) => {
    if (key === "name") return dir * (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
    return dir * (a[key] - b[key]);
  });
});

const visibleRows = computed<LocationSocialMetricsRow[]>(() => (props.limit == null ? searchedRows.value : searchedRows.value.slice(0, props.limit)));

const isShowingAllLink = computed<boolean>(() => props.limit != null && searchedRows.value.length > props.limit);

function formatCount(value: number): string {
  return NumberFormatting.abbreviatedFormat(value) || "0";
}

function formatRate(value: number): string {
  return NumberFormatting.formatPercent(value) || "—";
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Location social metrics table ==============================================
.location-social-metrics-table {
  box-sizing: border-box;
  padding: 22px 24px;
  border: 1px solid #e4e7ec;
  border-radius: 11px;
  background: #fff;

  &__heading {
    margin-bottom: 16px;
    gap: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
  }

  &__search {
    width: 200px;
    height: 32px;
    padding: 4px 10px;
    font-size: 14px;
  }

  &__scroll {
    max-width: 100%;
    overflow-x: hidden;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
  }

  &__header-row {
    border-bottom: 1px solid #f0f2f5;
  }

  &__th {
    padding: 0 10px 10px 0;
    color: #667085;
    font: 500 14px/18px sans-serif;
    text-align: left;
    white-space: nowrap;

    &--number {
      text-align: right;
    }

    &--networks {
      width: 96px;
    }

    &--action {
      width: 106px;
    }

    &--sort {
      cursor: pointer;

      &:hover {
        color: #102f41;
      }
    }
  }

  &__th-inner {
    gap: 5px;
    display: inline-flex;
    justify-content: flex-end;
    align-items: center;

    &--start {
      justify-content: flex-start;
    }
  }

  &__caret {
    flex-shrink: 0;
  }

  &__info {
    flex-shrink: 0;
    display: inline-flex;

    :deep(.info-icon) {
      min-width: 0;
      min-height: 0;

      &::before,
      &::after {
        display: none;
      }
    }
  }

  &__row {
    border-bottom: 1px solid #f0f2f5;

    &:last-child {
      border-bottom: 0;
    }
  }

  &__td {
    padding: 10px 10px 10px 0;
    color: #102f41;
    font: 400 14px/18px sans-serif;
    vertical-align: middle;

    &--number {
      font-family: $font-family;
      font-weight: 600;
      text-align: right;
      white-space: nowrap;
    }

    &--action {
      text-align: right;
      white-space: nowrap;
    }
  }

  &__location {
    gap: 11px;
    display: flex;
    align-items: center;
  }

  &__name {
    min-width: 0;
    font-weight: 500;
    overflow-wrap: anywhere;
  }

  &__muted {
    color: #667085;
  }

  &__empty-cell {
    padding: 18px 0;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__view-all {
    margin-top: 16px;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .location-social-metrics-table {
    padding: 18px 15px;

    &__search {
      width: 100%;
    }

    &__scroll {
      overflow-x: auto;
    }

    &__table {
      min-width: 620px;
    }
  }
}
</style>
